/**
 * Cookie utility functions for cross-domain cookie sharing
 * Sets cookies with domain .foundersmate.ai so they're accessible from both
 * foundersmate.ai and app.foundersmate.ai
 */

/**
 * Set a cookie with shared domain
 * @param name - Cookie name
 * @param value - Cookie value
 * @param days - Number of days until expiration (default: 7)
 */
export const setCookie = (name: string, value: string, days: number = 7): void => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  
  // Set cookie with domain .foundersmate.ai (leading dot allows subdomains)
  // This makes the cookie accessible from both foundersmate.ai and app.foundersmate.ai
  const cookieString = `${name}=${value}; expires=${expires.toUTCString()}; path=/; domain=.foundersmate.ai; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure' : ''}`;
  
  document.cookie = cookieString;
};

/**
 * Get a cookie value by name
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
export const getCookie = (name: string): string | null => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  
  return null;
};

/**
 * Delete a cookie
 * @param name - Cookie name
 */
export const deleteCookie = (name: string): void => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.foundersmate.ai;`;
};

/**
 * Set authentication cookies (token and user info)
 * @param token - Authentication token
 * @param user - User object
 */
export const setAuthCookies = (token: string, user: any): void => {
  setCookie('authToken', token, 7);
  setCookie('userInfo', JSON.stringify(user), 7);
};

/**
 * Clear authentication cookies
 */
export const clearAuthCookies = (): void => {
  deleteCookie('authToken');
  deleteCookie('userInfo');
};

/**
 * Get authentication token from cookie
 * @returns Token or null
 */
export const getAuthTokenFromCookie = (): string | null => {
  return getCookie('authToken');
};

/**
 * Get user info from cookie
 * @returns User object or null
 */
export const getUserInfoFromCookie = (): any | null => {
  const userInfo = getCookie('userInfo');
  if (userInfo) {
    try {
      return JSON.parse(userInfo);
    } catch (error) {
      console.error('Error parsing user info from cookie:', error);
      return null;
    }
  }
  return null;
};
